import { SerializedStyles } from "@emotion/react";
import cloneDeep from "clone-deep";
import { Ref, memo, useCallback } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage } from "src/components";
import DropdownSelector from "src/components/DropdownSelector";
import { DependentInfo } from "src/types/Application";
import { noInput } from "src/utilities/input";

import { formatters } from "@fraction/shared";

export interface DependentsProps {
  onChange: (value: DependentInfo[]) => void;
  value?: DependentInfo[];
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const Dependents = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  ...props
}: DependentsProps) => {
  const validate = useCallback(() => !noInput(value), [value]);
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const handleChangeNumberOfDependents = useCallback(
    (val: string) => {
      const numberOfDependents = formatters.number.toNumber(val);
      const newValue = cloneDeep(value || []);
      for (let i = 0; i < numberOfDependents; i++) {
        if (!newValue[i]) {
          newValue[i] = {};
        }
      }
      onChange?.(newValue.slice(0, numberOfDependents));
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title={title || "How many dependents do you have?"}
      description="If the person lives with you and relies on you for financial and physical support, they are a dependent."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <DropdownSelector
        focusRef={focusRef}
        value={value?.length.toString()}
        onChange={handleChangeNumberOfDependents}
        options={[
          { value: "0", label: "Zero" },
          { value: "1", label: "One" },
          { value: "2", label: "Two" },
          { value: "3", label: "Three" },
          { value: "4", label: "Four" },
          { value: "5", label: "Five" },
          { value: "6", label: "Six" },
          { value: "7", label: "Seven" },
          { value: "8", label: "Eight" },
          { value: "9", label: "Nine" },
          { value: "10", label: "Ten" },
        ]}
      />
    </FormFlowPage>
  );
};

export default memo(Dependents);
